import React from 'react';

const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.tsl.thesingingleague&hl=en';
const APP_STORE_URL = 'https://apps.apple.com/us/app/singing-league-ai-vocal-judge/id6755197560';

const AppleBadge: React.FC<{ className?: string }> = ({ className = '' }) => (
  <svg viewBox="0 0 120 40" className={className} xmlns="http://www.w3.org/2000/svg">
    <rect width="120" height="40" rx="6" fill="#000" />
    <rect x="0.5" y="0.5" width="119" height="39" rx="5.5" stroke="#A6A6A6" fill="none" strokeWidth="0.8" />
    <g fill="#fff">
      <path d="M24.77 20.3a4.95 4.95 0 0 1 2.36-4.15 5.07 5.07 0 0 0-3.99-2.16c-1.68-.18-3.31 1.01-4.17 1.01-.87 0-2.19-.99-3.61-.96a5.31 5.31 0 0 0-4.47 2.73c-1.93 3.34-.49 8.27 1.36 10.97.92 1.33 2 2.82 3.43 2.76 1.38-.06 1.9-.89 3.57-.89 1.66 0 2.13.89 3.58.86 1.48-.02 2.4-1.34 3.3-2.68a10.96 10.96 0 0 0 1.5-3.09 4.78 4.78 0 0 1-2.86-4.4z" />
      <path d="M22.04 12.21a4.87 4.87 0 0 0 1.12-3.49 4.96 4.96 0 0 0-3.21 1.66 4.64 4.64 0 0 0-1.14 3.37 4.11 4.11 0 0 0 3.23-1.54z" />
    </g>
    <g fill="#fff">
      <text x="42.5" y="15.5" fontSize="7.2" fontFamily="system-ui, -apple-system, sans-serif" letterSpacing="0.04em">Download on the</text>
      <text x="42.5" y="27" fontSize="13" fontFamily="system-ui, -apple-system, sans-serif" fontWeight="600" letterSpacing="-0.01em">App Store</text>
    </g>
  </svg>
);

const GooglePlayBadge: React.FC<{ className?: string }> = ({ className = '' }) => (
  <svg viewBox="0 0 135 40" className={className} xmlns="http://www.w3.org/2000/svg">
    <rect width="135" height="40" rx="6" fill="#000" />
    <rect x="0.5" y="0.5" width="134" height="39" rx="5.5" stroke="#A6A6A6" fill="none" strokeWidth="0.8" />
    <g transform="translate(10, 5)">
      <defs>
        <linearGradient id="gp-a" x1="14.54" y1="31.01" x2="-1.05" y2="15.42" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#00A0FF" />
          <stop offset=".01" stopColor="#00A1FF" />
          <stop offset=".26" stopColor="#00BEFF" />
          <stop offset=".51" stopColor="#00D2FF" />
          <stop offset=".76" stopColor="#00DFFF" />
          <stop offset="1" stopColor="#00E3FF" />
        </linearGradient>
        <linearGradient id="gp-b" x1="24.83" y2="15" x2="-2.15" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#FFE000" />
          <stop offset=".41" stopColor="#FFBD00" />
          <stop offset=".78" stopColor="#FFA500" />
          <stop offset="1" stopColor="#FF9C00" />
        </linearGradient>
        <linearGradient id="gp-c" x1="17.55" y1="17.32" x2="-5.28" y2="39.15" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#FF3A44" />
          <stop offset="1" stopColor="#C31162" />
        </linearGradient>
        <linearGradient id="gp-d" x1="-2.78" y1="-3.97" x2="8.41" y2="7.22" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#32A071" />
          <stop offset=".07" stopColor="#2DA771" />
          <stop offset=".48" stopColor="#15CF74" />
          <stop offset=".8" stopColor="#06E775" />
          <stop offset="1" stopColor="#00F076" />
        </linearGradient>
      </defs>
      <path d="M1.22 1.05A2.06 2.06 0 0 0 .69 2.56v24.88a2.06 2.06 0 0 0 .53 1.51l.08.08L14.92 15.4v-.31L1.3.97z" fill="url(#gp-a)" />
      <path d="M19.46 20l-4.54-4.55v-.31-.31l4.54-4.55.1.06 5.38 3.06c1.54.87 1.54 2.3 0 3.18l-5.38 3.06z" fill="url(#gp-b)" />
      <path d="M19.56 19.94L14.92 15.3.97 29.21a1.78 1.78 0 0 0 2.27.07l16.32-9.34" fill="url(#gp-c)" />
      <path d="M19.56 10.06L3.24.72a1.78 1.78 0 0 0-2.27.07L14.92 14.7z" fill="url(#gp-d)" />
    </g>
    <g fill="#fff">
      <text x="38" y="12.5" fontSize="5.8" fontFamily="system-ui, -apple-system, sans-serif" letterSpacing="0.05em">GET IT ON</text>
      <text x="38" y="27" fontSize="12.5" fontFamily="system-ui, -apple-system, sans-serif" fontWeight="600" letterSpacing="-0.02em">Google Play</text>
    </g>
  </svg>
);

interface AppStoreBadgesProps {
  size?: 'sm' | 'md';
  className?: string;
}

export const AppStoreBadges: React.FC<AppStoreBadgesProps> = ({ size = 'md', className = '' }) => {
  const badgeWidth = size === 'sm' ? 'w-[120px]' : 'w-[140px]';
  const googleWidth = size === 'sm' ? 'w-[135px]' : 'w-[157px]';

  return (
    <div className={`flex items-center justify-center gap-3 ${className}`}>
      <a
        href={APP_STORE_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="opacity-85 hover:opacity-100 transition-all duration-300 hover:scale-105"
      >
        <AppleBadge className={`${badgeWidth} h-auto`} />
      </a>
      <a
        href={PLAY_STORE_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="opacity-85 hover:opacity-100 transition-all duration-300 hover:scale-105"
      >
        <GooglePlayBadge className={`${googleWidth} h-auto`} />
      </a>
    </div>
  );
};
